import { consola } from 'consola'

/**
 * Единый генератор отпечатка устройства для всех методов авторизации.
 *
 * Две стратегии, выбор по платформе:
 *  - нативное приложение (Capacitor) — стабильный идентификатор устройства.
 *    На Android это `Settings.Secure.ANDROID_ID`, на iOS `identifierForVendor`;
 *    оба переживают переустановку приложения. Дублируется в SecureStorage,
 *    чтобы отпечаток не терялся, если платформа не отдала идентификатор.
 *  - веб — `visitorId` от FingerprintJS (canvas/WebGL/audio/шрифты/железо).
 *
 * На сервере (SSR) и при любой ошибке возвращает `undefined`: отпечаток —
 * вспомогательный сигнал, он не должен ронять вход.
 */

const logger = consola.withTag('clientFingerprint')

/** Ключ SecureStorage. Намеренно НЕ входит в список очистки `useAppReset`. */
const DEVICE_ID_STORAGE_KEY = 'tasteok_device_id'

/** Префиксы версии схемы: бэк по ним отличает нативный отпечаток от веб-овского. */
const NATIVE_PREFIX = 'n2'
const WEB_PREFIX = 'w2'

/** Отпечаток стабилен в пределах вкладки/сессии — считаем один раз. */
let inflight: Promise<string | undefined> | null = null

const readStoredDeviceId = async (): Promise<string | undefined> => {
  try {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
    const result = await SecureStoragePlugin.get({ key: DEVICE_ID_STORAGE_KEY })
    return result?.value || undefined
  } catch {
    // Ключа ещё нет — это штатная ситуация при первом запуске.
    return undefined
  }
}

const writeStoredDeviceId = async (value: string): Promise<void> => {
  try {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
    await SecureStoragePlugin.set({ key: DEVICE_ID_STORAGE_KEY, value })
  } catch (error) {
    logger.warn('Не удалось сохранить идентификатор устройства', error)
  }
}

const createRandomId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`
}

/**
 * Нативный отпечаток. Идентификатор платформы приоритетнее сохранённого:
 * он переживает очистку данных приложения, а SecureStorage — нет.
 */
const resolveNativeFingerprint = async (): Promise<string | undefined> => {
  const stored = await readStoredDeviceId()

  let platformId: string | undefined
  try {
    const { Device } = await import('@capacitor/device')
    const { identifier } = await Device.getId()
    platformId = identifier || undefined
  } catch (error) {
    logger.debug('Device.getId() недоступен, используем резервный идентификатор', error)
  }

  const deviceId = platformId || stored || createRandomId()

  if (stored !== deviceId) {
    await writeStoredDeviceId(deviceId)
  }

  return `${NATIVE_PREFIX}_${deviceId}`
}

/** Веб-отпечаток через FingerprintJS (open-source сборка). */
const resolveWebFingerprint = async (): Promise<string | undefined> => {
  const FingerprintJS = await import('@fingerprintjs/fingerprintjs')
  const agent = await FingerprintJS.load()
  const { visitorId } = await agent.get()

  if (!visitorId) return undefined

  return `${WEB_PREFIX}_${visitorId}`
}

const resolveFingerprint = async (): Promise<string | undefined> => {
  const { Capacitor } = await import('@capacitor/core')

  return Capacitor.isNativePlatform()
    ? await resolveNativeFingerprint()
    : await resolveWebFingerprint()
}

/**
 * Возвращает отпечаток текущего устройства.
 * Результат кэшируется на время жизни страницы/приложения.
 */
export const getClientFingerprint = async (): Promise<string | undefined> => {
  if (typeof window === 'undefined') return undefined

  if (!inflight) {
    inflight = resolveFingerprint().catch(error => {
      logger.warn('Не удалось вычислить отпечаток устройства', error)
      // Сбрасываем кэш, чтобы следующая попытка входа посчитала заново.
      inflight = null
    })
  }

  return await inflight
}

/** Только для тестов: сбрасывает кэш отпечатка. */
export const resetClientFingerprintCache = (): void => {
  inflight = null
}
